/**
 * Display helpers. None of these compute anything the API doesn't already own.
 * they format values for lists and tables.
 */

export function timeAgo(iso: string | null): string {
    if (!iso) return 'never';

    const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (seconds < 60) return 'just now';

    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;

    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

    const days = Math.round(hours / 24);
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;

    return new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function shortDate(iso: string | null): string {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Absolute local stamp for when a page was scraped / a demo snapshot was written.
 * Relative age is `timeAgo`; this is the fixed clock reading next to it.
 */
export function formatDateTime(iso: string | null): string {
    if (!iso) return '-';
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    } catch {
        return iso;
    }
}

/**
 * Format a money amount. Prefer the row's currency; fall back to the country
 * pool expected code. Never invent a currency code when both are missing:
 * show the bare number rather than mislabeling as USD.
 */
export function formatMoney(
    amount: number | null | undefined,
    currency: string | null | undefined,
    poolCurrency?: string | null,
): string {
    if (amount == null) return '-';
    const code = currency || poolCurrency || null;
    if (!code) return amount.toFixed(2);
    try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(amount);
    } catch {
        return `${code} ${amount.toFixed(2)}`;
    }
}
